import { zodResolver } from '@hookform/resolvers/zod';
import { Link } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Button, FormField, Input } from '@/shared/components';
import { AuthAlert } from './AuthAlert';
import type { AuthFailure } from './loginFailure';
import { PasswordInput } from './PasswordInput';

const schema = z.object({
  tenantSlug: z
    .string()
    .min(1, 'Gib dein Unternehmenskürzel ein.')
    .regex(/^[a-z0-9-]+$/, 'Nur Kleinbuchstaben, Ziffern und Bindestriche.'),
  email: z.string().email('Gib eine gültige E-Mail-Adresse ein.'),
  password: z.string().min(1, 'Gib dein Passwort ein.'),
});

export type LoginValues = z.infer<typeof schema>;

/** Anmeldeformular (Board A): Kürzel, E-Mail, Passwort; ein Fehler steht als Karte über dem Formular. */
export function LoginPasswordForm({
  onSubmit,
  failure,
  defaultTenantSlug = '',
}: {
  onSubmit: (values: LoginValues) => Promise<void>;
  failure: AuthFailure | null;
  /** Aus dem Einladungslink; der Fokus springt dann gleich zur E-Mail. */
  defaultTenantSlug?: string;
}) {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginValues>({
    resolver: zodResolver(schema),
    defaultValues: { tenantSlug: defaultTenantSlug, email: '', password: '' },
  });
  const slugGiven = defaultTenantSlug.length > 0;

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-[18px]" noValidate>
      {failure && <AuthAlert title={failure.title} text={failure.text} />}
      <FormField
        label="Unternehmenskürzel"
        error={errors.tenantSlug?.message}
        hint="Steht in deiner Einladungs-Mail. Nur Kleinbuchstaben, Ziffern und Bindestriche."
        required
      >
        <Input
          {...register('tenantSlug')}
          autoComplete="organization"
          autoCapitalize="none"
          spellCheck={false}
          autoFocus={!slugGiven}
          className="font-mono"
        />
      </FormField>
      <FormField label="E-Mail" error={errors.email?.message} required>
        <Input {...register('email')} type="email" autoComplete="email" autoFocus={slugGiven} />
      </FormField>
      <FormField label="Passwort" error={errors.password?.message} required>
        <PasswordInput {...register('password')} autoComplete="current-password" />
      </FormField>
      <Link
        to="/forgot-password"
        className="text-forest focus-visible:outline-forest inline-flex min-h-11 items-center self-end text-sm font-semibold underline-offset-4 hover:underline"
      >
        Passwort vergessen?
      </Link>
      <Button
        type="submit"
        variant="brand"
        size="lg"
        className="h-[54px] w-full text-base"
        loading={isSubmitting}
      >
        {isSubmitting ? 'Wird angemeldet …' : 'Anmelden'}
      </Button>
      <p className="text-ink-soft text-[13px] leading-relaxed">
        Neu im Team? Den Link zum Aktivieren schickt dir dein Administrator per E-Mail.
      </p>
    </form>
  );
}
